import React, { useState, useEffect, useCallback, useRef } from "react"
import PageHeader from "../components/Common/PageHeader"
import EmptyState from "../components/Common/EmptyState"
import AddUserModal from "../components/Users/AddUserModal"
import { useDispatch, useSelector } from "react-redux"
import Table from "../components/Common/Table"
import Dropdown from "../components/Common/Dropdown"
import { columns } from "../utils/TableData/InvitedUser/InvitedUserColumns"
import {
  actions,
  readOnlyActions,
} from "../utils/TableData/InvitedUser/InvitedUserActionsTable"
import useUserInvites from "../services/useUserInvites"
import {
  Spin,
  message,
  Pagination,
  Button,
  Dropdown as AntDropdown,
  Modal,
} from "antd"
import { DownloadOutlined, DownOutlined } from "@ant-design/icons"
import UpdateUserRoleModal from "../components/Users/UpdateUserRoleModal"
import { getUserCookies } from "../utils/Cookies"
import StoreDrawer from "../components/Common/StoreDrawer"
import { jwtDecode } from "jwt-decode"
import { fetchApprovedMerchants } from "../ReduxToolKit/Slice/MerchantsSlice"
import DeleteConfirmationModal from "../components/Common/Modals/DeleteConfirmationModal"
import { translations } from "../utils/Translation/staticTranslation"
import { Invites } from "../Apis/invitesApis"
import {
  effectiveHighestRole,
  isProfileModuleDeleteBlocked,
  isProfileModuleReadOnly,
} from "../utils/modulePermissionSidebar"

/** `null` = all merchants (no `business` query param). */
function merchantArgForFetch(selectMerchant) {
  if (selectMerchant === "all" || selectMerchant === "") return null
  if (selectMerchant && typeof selectMerchant === "object") return selectMerchant
  return null
}

function Users() {
  const dispatch = useDispatch()
  const searchTimeoutRef = useRef(null)
  const isInitialMount = useRef(true)

  const token = getUserCookies()?.token
  const decoded = token ? jwtDecode(token) : null
  const highest_role = decoded?.highest_role
  const market_role = decoded?.market_role
  const isMarketRoleThree = market_role === "3"

  const user = useSelector((state) => state.user.user)
  const isReadOnly =
    market_role === "2" ||
    market_role === "3" ||
    isProfileModuleReadOnly(user, "users")
  const canDelete = !isReadOnly && !isProfileModuleDeleteBlocked(user, "users")
  const effectiveHighest = effectiveHighestRole(user, highest_role)

  const isGlobalMerchantPickerRole =
    effectiveHighest === "SUPERADMIN" ||
    highest_role === "SUPERADMIN" ||
    market_role === "2" ||
    market_role === "3"

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [updateRoleModal, setUpdateRoleModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(false)
  const [selectMerchant, setSelectMerchant] = useState(
    isGlobalMerchantPickerRole ? "all" : "",
  ) // Default to 'all' for superadmin / market viewers
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [searchValue, setSearchValue] = useState("") // Controlled search input value
  const [searching, setSearching] = useState(false) // Indicates if search is in progress
  const [skipEffectTrigger, setSkipEffectTrigger] = useState(false) // Flag to skip useEffect triggers
  const [exportLoading, setExportLoading] = useState(false)
  const [isViewModalVisible, setIsViewModalVisible] = useState(false)
  const [viewLoading, setViewLoading] = useState(false)
  const [userDetail, setUserDetail] = useState(null)

  const loading = useSelector((state) => state.user.loading)
  const merchants = useSelector((state) => state.merchant.merchants)

  const lang = localStorage.getItem("language") || "ar"
  const userPageContent = translations[lang]?.userPage
  const filters = translations[lang]?.filters
  const modal = translations[lang]?.modals
  const { common_error_msg } = translations[lang]

  // Use custom hook with modified behavior to avoid double fetching
  const {
    selectedCategory,
    setUserStatus,
    handleUpdateUserStatus,
    initialLoading,
    userInvites,
    handleDeleteInvitedUser,
    setSearchTerm,
    setSelectedCategory,
    setFilterStatus,
    setUserInvites,
    filterStatus,
    updateLoading,
    fetchInvites,
    disableAutoFetch, // We'll control the fetching from component
    setDisableAutoFetch,
    deleteLoading,
    pagination,
    setPagination,
  } = useUserInvites(merchantArgForFetch(selectMerchant), user?.business_id, loading)

  // Set disableAutoFetch to true on first render
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      setDisableAutoFetch(true)
    }
  }, [setDisableAutoFetch])

  // Prevent duplicate merchant fetch
  const hasFetchedMerchants = useRef(false)
  
  useEffect(() => {
    if (
      (highest_role === "SUPERADMIN" || isReadOnly) &&
      !hasFetchedMerchants.current &&
      (!merchants || merchants.length === 0)
    ) {
      hasFetchedMerchants.current = true
      dispatch(fetchApprovedMerchants())
    }
  }, [dispatch, highest_role, isReadOnly, merchants])

  const showUpdateRoleModal = (row) => {
    setUpdateRoleModal(true)
    setSelectedUser(row)
  }
  const showModal = () => setIsModalOpen(true)
  const handleCancel = () => {
    setIsModalOpen(false)
    setUpdateRoleModal(false)
  }

  const handleDeleteMerchantModalOpen = (row) => {
    if (!canDelete) return
    setSelectedRow(row)
    setIsDeleteModalVisible(true)
  }
  const handleDeleteConfirmationModalClose = () => {
    setSelectedRow({})
    setIsDeleteModalVisible(false)
  }
  const deleteMerchant = () => {
    if (!canDelete) {
      return message.error(
        common_error_msg?.delete_not_allowed ||
          "Your role does not allow deleting in this section."
      )
    }
    if (selectedRow.id) {
      handleDeleteInvitedUser(selectedRow.id)
    } else return message.error(common_error_msg?.select_user_to_delete)
  }

  // Effect to fetch data when selectMerchant changes - we keep this one
  useEffect(() => {
    if (!loading && isGlobalMerchantPickerRole && !skipEffectTrigger) {
      // Reset to page 1 when merchant changes
      setPagination((prev) => ({ ...prev, current: 1 }))
      // Force a data fetch when the merchant selection changes
      fetchInvites(merchantArgForFetch(selectMerchant), "", "", "", 1)
    }

    // Reset the flag
    if (skipEffectTrigger) {
      setSkipEffectTrigger(false)
    }
  }, [
    selectMerchant,
    loading,
    skipEffectTrigger,
    isGlobalMerchantPickerRole,
    fetchInvites,
    setPagination,
  ])

  // Function to get the button text for PageHeader
  // Super Admin / Owner / Admin (multi-merchant): no Select Merchant (lists all by default)
  // Merchant-scoped Owner / Admin keep Add User
  const getHeaderButtonText = () => {
    if (effectiveHighest === "SUPERADMIN" || highest_role === "SUPERADMIN") {
      return ""
    }
    if (highest_role === "OWNER" || highest_role === "ADMIN") {
      return userPageContent?.add_user
    }
    // Market viewers: no Select Merchant header button
    if (isReadOnly) {
      return ""
    }
    return ""
  }

  // Handle category dropdown change
  const handleCategoryChange = useCallback(
    (value) => {
      setSkipEffectTrigger(true) // Set flag to avoid double fetching
      setSelectedCategory(value)
      setSearchValue("") // Reset search input field
      setSearchTerm("") // Reset search term in the hook

      // Reset to page 1 when category changes
      setPagination((prev) => ({ ...prev, current: 1 }))
      // Fetch data with new category and empty search
      fetchInvites(
        merchantArgForFetch(selectMerchant),
        value,
        "",
        filterStatus,
        1
      )
    },
    [selectMerchant, filterStatus, fetchInvites, setSearchTerm]
  )

  // Handle filter status dropdown change
  const handleFilterStatusChange = useCallback(
    (value) => {
      setSkipEffectTrigger(true) // Set flag to avoid double fetching
      const newFilterStatus = value === "All" ? "" : value
      setFilterStatus(newFilterStatus)

      // Reset to page 1 when filter changes
      setPagination((prev) => ({ ...prev, current: 1 }))
      // Fetch data with new filter status
      fetchInvites(
        merchantArgForFetch(selectMerchant),
        selectedCategory,
        searchValue,
        newFilterStatus,
        1
      )
    },
    [selectMerchant, selectedCategory, searchValue, fetchInvites]
  )

  // Handle search input change with debounce
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value
      setSearchValue(value) // Update local state immediately for UI

      // Clear any existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      // Show searching indicator
      if (value !== "") {
        setSearching(true)
      } else {
        setSearching(false)
      }

      // Set a new timeout
      searchTimeoutRef.current = setTimeout(() => {
        setSkipEffectTrigger(true) // Set flag to avoid double fetching
        setSearchTerm(value) // Update the hook state after delay

        // Reset to page 1 when searching
        setPagination((prev) => ({ ...prev, current: 1 }))
        // Only fetch if we have a category selected
        if (selectedCategory) {
          fetchInvites(
            merchantArgForFetch(selectMerchant),
            selectedCategory,
            value,
            filterStatus,
            1
          )
        }

        setSearching(false) // Hide searching indicator
      }, 800) // 800ms delay for search typing
    },
    [
      selectMerchant,
      selectedCategory,
      filterStatus,
      fetchInvites,
      setSearchTerm,
    ]
  )

  // Handle merchant selection
  const handleSelectMerchant = useCallback((merchant) => {
    setSkipEffectTrigger(true) // Set flag to avoid double fetching
    setSelectMerchant(merchant)
    setDrawerVisible(false)
  }, [])

  const handleViewUserDetails = (row) => {
    if (!row?.id) {
      message.error("User ID is missing for this merchant user.")
      return
    }

    const userFromList = userInvites.find((user) => user.id === row.id) || row
    setIsViewModalVisible(true)
    setViewLoading(false)
    setUserDetail(userFromList)
  }

  const closeViewModal = () => {
    setIsViewModalVisible(false)
    setUserDetail(null)
  }

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const escapeCsvField = (value) => {
    if (value === null || value === undefined) return ""
    const normalized = String(value).replace(/"/g, '""')
    return /[",\n]/.test(normalized) ? `"${normalized}"` : normalized
  }

  const handleExportCsv = async () => {
    setExportLoading(true)
    try {
      const payload = {}
      if (selectedCategory === "Name" && searchValue) {
        payload.name = searchValue
      } else if (selectedCategory === "Role" && searchValue) {
        payload.role = searchValue
      }
      if (filterStatus) {
        payload.status = filterStatus
      }
      if (
        selectMerchant &&
        selectMerchant !== "all" &&
        typeof selectMerchant === "object"
      ) {
        payload.business = selectMerchant.id
      }

      const response = await Invites.exportUserListCsv(payload)
      const blob = response?.data
      if (!(blob instanceof Blob)) {
        message.error("Could not export users CSV.")
        return
      }

      const contentDisposition = response?.headers?.["content-disposition"]
      const fileNameMatch = contentDisposition?.match(
        /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i
      )
      const fallback = `merchant-users-${new Date()
        .toISOString()
        .split("T")[0]}.csv`
      const fileName = fileNameMatch?.[1]?.trim() || fallback

      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download = fileName
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.URL.revokeObjectURL(url)
      message.success("CSV exported successfully.")
    } catch (error) {
      message.error("Failed to export users CSV.")
    } finally {
      setExportLoading(false)
    }
  }

  const getStoreName = (details) =>
    details?.store_name ||
    details?.store?.name ||
    details?.store?.name_english ||
    details?.business_name ||
    details?.business?.name_english ||
    "-"

  const getMerchantName = (details) =>
    details?.merchant_name ||
    details?.business?.name_english ||
    details?.business_name ||
    details?.name_english ||
    "-"

  const getPhoneNumber = (details) =>
    details?.phone_number || details?.phone || details?.mobile_number || "-"

  const getWebsite = (details) =>
    details?.website_link ||
    details?.website ||
    details?.business?.website_link ||
    details?.business?.website ||
    "-"

  const getEcommercePlatform = (details) =>
    details?.e_commerce_platform ||
    details?.ecommerce_platform ||
    details?.platform ||
    details?.business?.e_commerce_platform ||
    details?.business?.ecommerce_platform ||
    "-"

  const viewRows = [
    { label: lang === "ar" ? "اسم البائع الكامل" : "Vendor Full Name", value: userDetail?.name_english || "N/A" },
    { label: lang === "ar" ? "عنوان البريد الإلكتروني" : "Email Address", value: userDetail?.email_address || "N/A" },
    { label: lang === "ar" ? "رقم الهاتف" : "Phone Number", value: getPhoneNumber(userDetail) },
    { label: lang === "ar" ? "اسم التاجر" : "Merchant Name", value: getMerchantName(userDetail) },
    { label: lang === "ar" ? "اسم المتجر" : "Store Name", value: getStoreName(userDetail) },
    { label: lang === "ar" ? "منصة التجارة الإلكترونية" : "E-commerce Platform", value: getEcommercePlatform(userDetail) },
    { label: lang === "ar" ? "رابط الموقع" : "Business Url", value: getWebsite(userDetail), isLink: true },
  ]

  return (
    <div>
      <DeleteConfirmationModal
        deleteLoading={deleteLoading}
        visible={isDeleteModalVisible}
        onCancel={handleDeleteConfirmationModalClose}
        onConfirm={deleteMerchant}
        title={modal?.confirm_delete_text}
        content={
          <>
            {modal?.description_1} <b>{selectedRow?.name_english}</b>{" "}
            {modal?.description_2}
          </>
        }
      />
      <Modal
        open={isViewModalVisible}
        onCancel={closeViewModal}
        width={1000}
        footer={
          <div style={{ textAlign: "right" }}>
            <Button
              onClick={closeViewModal}
              style={{
                borderRadius: "24px",
                height: "44px",
                minWidth: "100px",
                borderColor: "#E4E7EC",
                color: "#667085",
                background: "#fff",
              }}
            >
              {lang === "ar" ? "إغلاق" : "Close"}
            </Button>
          </div>
        }
        title={getStoreName(userDetail) !== "-" ? getStoreName(userDetail) : (lang === "ar" ? "تفاصيل المستخدم" : "User Details")}
      >
        {viewLoading ? (
          <div className="d-flex justify-content-center align-items-center p-24">
            <Spin />
          </div>
        ) : (
          <div
            style={{
              border: "1px solid #EAECF0",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            {viewRows.map((item) => (
              <div
                key={item.label}
                style={{
                  display: "grid",
                  gridTemplateColumns: "180px 1fr",
                  borderBottom: "1px solid #EAECF0",
                }}
              >
                <div
                  style={{
                    padding: "16px",
                    borderRight: "1px solid #EAECF0",
                    color: "#344054",
                  }}
                >
                  {item.label}
                </div>
                <div style={{ padding: "16px", color: "#101828" }}>
                  {item.isLink && item.value && item.value !== "-" && item.value !== "N/A" ? (
                    <a href={item.value} target="_blank" rel="noreferrer">
                      {item.value}
                    </a>
                  ) : (
                    item.value || "N/A"
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <PageHeader
        user={user}
        title={userPageContent?.user}
        buttonText={getHeaderButtonText()}
        onClick={
          effectiveHighest === "SUPERADMIN" ||
          highest_role === "SUPERADMIN" ||
          isReadOnly
            ? undefined
            : showModal
        }
        buttonDisabled={
          isReadOnly &&
          (highest_role === "OWNER" || highest_role === "ADMIN")
        }
        buttonDisabledTitle={translations[lang]?.Referrals?.read_only_hint}
      />

      <div className="main-content-container">
        <div className="d-flex mb-24 align-items-center">
          {/* Select Dropdown */}
          <Dropdown
            selectedCategory={selectedCategory}
            label={filters?.select}
            iconSrc="/icons/selectstoreanalytics.svg"
            options={[
              { label: userPageContent?.dropdown_1?.name, value: "Name" },
              { label: userPageContent?.dropdown_1?.role, value: "Role" },
            ]}
            onSelect={handleCategoryChange}
          />

          {/* Search Input with indicator */}
          <div className={`${lang === "ar" ? "ml-10" : "mr-10"} relative`}>
            <input
              type="search"
              className="search-store"
              placeholder={filters?.search}
              value={searchValue}
              onChange={handleSearchChange}
            />
            {/* {searching && (
              <span
                className='absolute'
                style={{
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '12px',
                  color: '#888',
                }}
              >
                {filters?.searching || 'Searching...'}
              </span>
            )} */}
          </div>

          {/* Status Dropdown */}
          <Dropdown
            selectedCategory={filterStatus || filters?.all}
            label={filters?.all}
            iconSrc="/icons/selectstoreanalytics.svg"
            options={[
              { label: userPageContent?.dropdown_2?.all, value: "All" },
              { label: userPageContent?.dropdown_2?.active, value: "Active" },
              {
                label: userPageContent?.dropdown_2?.inactive,
                value: "Inactive",
              },
            ]}
            onSelect={handleFilterStatusChange}
          />
          {!isMarketRoleThree && (
            <div style={{ marginLeft: "auto" }}>
              <AntDropdown
                overlayStyle={{
                  borderRadius: "8px",
                  overflow: "hidden",
                  background: "#F2F4F7",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
                menu={{
                  items: [
                    {
                      key: "csv",
                      label: (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: "14px",
                            color: "#101828",
                          }}
                        >
                          <DownloadOutlined style={{ fontSize: 16 }} />
                          {translations[lang]?.export_csv || "CSV File"}
                        </div>
                      ),
                    },
                  ],
                  onClick: (e) => {
                    if (e.key === "csv") handleExportCsv()
                  },
                }}
                trigger={["click"]}
              >
                <Button
                  type="primary"
                  loading={exportLoading}
                  style={{
                    backgroundColor: "#3E3A36",
                    color: "#fff",
                    borderRadius: "8px",
                    height: "36px",
                    fontWeight: 500,
                    padding: "0 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    borderColor: "#3E3A36",
                  }}
                >
                  {translations[lang]?.export || "Export"}
                  <DownOutlined style={{ fontSize: 12 }} />
                </Button>
              </AntDropdown>
            </div>
          )}
        </div>

        {initialLoading || searching ? (
          <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            <Spin size="large" />
          </div>
        ) : isGlobalMerchantPickerRole &&
          selectMerchant !== "all" &&
          selectMerchant !== "" &&
          !(selectMerchant && typeof selectMerchant === "object") ? (
          // Global merchant-user viewers only: waiting for an explicit merchant scope
          <p className="text-center text-14 text-A1A1A1">
            {userPageContent?.select_merchant_view_user}
          </p>
        ) : userInvites.length > 0 ? (
          // Show table if users exist
          <>
            <Table
              columns={columns}
              data={userInvites}
              actions={
                highest_role === "OWNER" && !isReadOnly
                  ? actions(
                      handleViewUserDetails,
                      handleUpdateUserStatus,
                      showUpdateRoleModal,
                      updateLoading,
                      handleDeleteMerchantModalOpen,
                      canDelete
                    )
                  : readOnlyActions(handleViewUserDetails)
              } // No actions for non-owners, superadmin, or read-only (market_role 2)
            />
            {pagination.total > 0 && (
              <div className="d-flex justify-content-end mt-24">
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  showSizeChanger={false}
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total}`
                  }
                  onChange={(page) => {
                    setPagination((prev) => ({ ...prev, current: page }))
                    fetchInvites(
                      merchantArgForFetch(selectMerchant),
                      selectedCategory,
                      searchValue,
                      filterStatus,
                      page
                    )
                  }}
                />
              </div>
            )}
          </>
        ) : (
          // Show EmptyState if no users exist
          <EmptyState
            title={userPageContent?.no_user?.title}
            description={userPageContent?.no_user?.description}
            onClick={isReadOnly ? undefined : showModal}
          />
        )}
      </div>
      <AddUserModal isModalOpen={isModalOpen} handleCancel={handleCancel} />
      <UpdateUserRoleModal
        isModalOpen={updateRoleModal}
        handleCancel={handleCancel}
        selectedUser={selectedUser}
        setUserInvites={setUserInvites}
      />

      <StoreDrawer
        highest_role={highest_role}
        market_role={market_role}
        title={userPageContent?.select_merchant}
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        stores={merchants}
        selectMerchant={selectMerchant}
        setSelectMerchant={handleSelectMerchant}
        AllFilterOption={userPageContent?.merchants}
      />
    </div>
  )
}

export default Users
